import {Button, Checkbox, Popover, Stack, Text} from '@mantine/core'
import {IconChevronDown} from '@tabler/icons-react'

type FilterMultiSelectButtonProps<TValue extends string> = {
    /** Name of the filter, used as the button label prefix and accessible name. */
    label: string
    /** Explains the match rule; shown once at the top of the dropdown. */
    description?: string
    options: Array<{value: TValue; label: string}>
    value: TValue[]
    primaryValue?: TValue
    onChange: (value: TValue[]) => void
}

/**
 * Renders the selected values as the button's own label ("Status: Estimate +1") so a
 * list's effective filter state is readable without opening anything. An empty
 * selection falls back to the bare filter name.
 */
export function multiSelectFilterLabel<TValue extends string>(
    label: string,
    options: Array<{value: TValue; label: string}>,
    value: TValue[],
    primaryValue?: TValue,
) {
    if (value.length === 0) {
        return label
    }

    const orderedValue =
        primaryValue && value.length > 1 && value.includes(primaryValue)
            ? [primaryValue, ...value.filter((entry) => entry !== primaryValue)]
            : value
    const [first, ...rest] = orderedValue
    const firstLabel = options.find((option) => option.value === first)?.label ?? first
    const suffix = rest.length > 0 ? ` +${rest.length}` : ''
    return `${label}: ${firstLabel}${suffix}`
}

export function FilterMultiSelectButton<TValue extends string>({
    label,
    description,
    options,
    value,
    primaryValue,
    onChange,
}: FilterMultiSelectButtonProps<TValue>) {
    const buttonLabel = multiSelectFilterLabel(label, options, value, primaryValue)

    return (
        <Popover width={260} position="bottom-start">
            <Popover.Target>
                <Button
                    aria-label={`${label} filter`}
                    title={buttonLabel}
                    variant={value.length > 0 ? 'light' : 'default'}
                    rightSection={<IconChevronDown size={16} stroke={1.5} />}
                    maw={200}
                    styles={{label: {overflow: 'hidden', textOverflow: 'ellipsis'}}}
                >
                    {buttonLabel}
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    {description ? (
                        <Text size="xs" c="dimmed">
                            {description}
                        </Text>
                    ) : null}
                    <Checkbox.Group
                        value={value}
                        onChange={(selected) => onChange(selected as TValue[])}
                    >
                        <Stack gap="xs">
                            {options.map((option) => (
                                <Checkbox
                                    key={option.value}
                                    value={option.value}
                                    label={option.label}
                                />
                            ))}
                        </Stack>
                    </Checkbox.Group>
                    <Button
                        aria-label={`Clear ${label.toLowerCase()} filter`}
                        disabled={value.length === 0}
                        variant="light"
                        size="compact-sm"
                        onClick={() => onChange([])}
                    >
                        Clear
                    </Button>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    )
}

type FilterToggleButtonProps = {
    /** Short label shown in the bar. */
    label: string
    /** Full description of what the filter does, used as the accessible name. */
    accessibleLabel: string
    checked: boolean
    onChange: (checked: boolean) => void
}

export function FilterToggleButton({
    label,
    accessibleLabel,
    checked,
    onChange,
}: FilterToggleButtonProps) {
    return (
        <Button
            aria-label={accessibleLabel}
            aria-pressed={checked}
            variant={checked ? 'light' : 'default'}
            onClick={() => onChange(!checked)}
        >
            {label}
        </Button>
    )
}
